import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '../ui-pages.css';
import { usePermissions } from '../context/PermissionsContext';
import { PAGES } from '../utils/pages';

const Users = () => {
  const { workspaceUuid } = useParams();
  const { role: myRole } = usePermissions();
  const isAdminUser = myRole === 'Admin';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'Member',
    pages: []
  });

  const togglePage = (key) => setFormData(fd => ({
    ...fd,
    pages: fd.pages.includes(key) ? fd.pages.filter(p => p !== key) : [...fd.pages, key]
  }));

  const [roleFilter, setRoleFilter] = useState('All');
  const [search, setSearch] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/workspace-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/users/workspace-users/${editingUser._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/users/workspace-users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', role: 'Member', pages: [] });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      try {
        await axios.delete(`${API_URL}/api/users/workspace-users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchUsers();
      } catch (err) {
        setError(err.response?.data?.message || 'Delete failed');
      }
    }
  };

  // ---- presentation helpers (no logic change) ----
  const AVATAR_COLORS = ['#111111'];
  const colorFor = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 9973;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
  };
  const initialsFor = (email = '') => {
    const name = email.split('@')[0] || '';
    const parts = name.split(/[._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase() || '?';
  };
  const isAdmin = (role = '') => /admin/i.test(role);
  const rolePill = (role = '') => isAdmin(role)
    ? { color: '#111111', background: '#FAF7F1' }
    : { color: '#6B6862', background: '#FAF7F1' };

  const adminCount = users.filter(u => isAdmin(u.role)).length;
  const memberCount = users.length - adminCount;

  const filtered = users.filter(u => {
    const matchesRole =
      roleFilter === 'All' ? true :
      roleFilter === 'Admins' ? isAdmin(u.role) :
      !isAdmin(u.role);
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || u.email.toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
    return matchesRole && matchesSearch;
  });

  if (loading) {
    return (
      <div className="ui-page">
        <header className="ui-head">
          <div>
            <h1 className="ui-title">Team</h1>
            <p className="ui-sub">Manage your organization's members and their access levels.</p>
          </div>
        </header>
        <div className="tm-grid cl-skel-stagger">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cl-skel-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div className="cl-skel cl-skel-circle" style={{ width: 52, height: 52 }} />
                <div className="cl-skel" style={{ width: 70, height: 22, borderRadius: 999 }} />
              </div>
              <div className="cl-skel" style={{ width: '55%', height: 15, borderRadius: 6 }} />
              <div className="cl-skel" style={{ width: '75%', height: 10, borderRadius: 6, marginTop: 8 }} />
              <div className="cl-skel" style={{ width: '40%', height: 9, borderRadius: 6, marginTop: 12 }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid #E7E1D6' }}>
                <div className="cl-skel" style={{ width: 64, height: 28, borderRadius: 10 }} />
                <div className="cl-skel" style={{ width: 64, height: 28, borderRadius: 10 }} />
              </div>
            </div>
          ))}
        </div>
        <style>{tmCss}</style>
      </div>
    );
  }

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Team</h1>
          <p className="ui-sub">Manage your organization's members and their access levels.</p>
        </div>
        <div className="ui-head-actions">
          {isAdminUser && <button
            className="ui-btn"
            onClick={() => { setEditingUser(null); setFormData({ email: '', password: '', role: 'Member', pages: [] }); setShowModal(true); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            Add member
          </button>}
        </div>
      </header>

      {/* stats */}
      <div className="ui-stats">
        <div className="ui-stat tone-slate">
          <div className="ui-stat-ic">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <div className="ui-stat-n">{users.length}</div>
            <div className="ui-stat-l">Total members</div>
          </div>
        </div>
        <div className="ui-stat tone-violet">
          <div className="ui-stat-ic">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3 6 6 .9-4.5 4.3 1.1 6.3L12 17l-5.7 2.5 1.1-6.3L3 8.9 9 8z" />
            </svg>
          </div>
          <div>
            <div className="ui-stat-n">{adminCount}</div>
            <div className="ui-stat-l">Administrators</div>
          </div>
        </div>
        <div className="ui-stat tone-blue">
          <div className="ui-stat-ic">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <div className="ui-stat-n">{memberCount}</div>
            <div className="ui-stat-l">Members</div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: '#F6E4E1', color: '#B23A3A', padding: '12px 16px', borderRadius: '14px', marginBottom: '18px', fontSize: '14px', border: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* filter tabs */}
      <div className="ui-tabs">
        {[
          { key: 'All', label: 'All', n: users.length },
          { key: 'Admins', label: 'Admins', n: adminCount },
          { key: 'Members', label: 'Members', n: memberCount }
        ].map(t => (
          <button
            key={t.key}
            className={`ui-tab${roleFilter === t.key ? ' on' : ''}`}
            onClick={() => setRoleFilter(t.key)}
          >
            {t.label}
            <span className="ui-tab-n">{t.n}</span>
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <div className="ui-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* member list */}
      {filtered.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </div>
          <h2>No members found</h2>
          <p>{users.length === 0 ? 'Invite your first teammate to start collaborating.' : 'No members match your current filter or search.'}</p>
          {isAdminUser && <button className="ui-btn" onClick={() => { setEditingUser(null); setFormData({ email: '', password: '', role: 'Member', pages: [] }); setShowModal(true); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add member
          </button>}
        </div>
      ) : (
        <div className="tm-grid">
          {filtered.map((user, i) => (
            <div key={user._id} className="tm-card" style={{ animationDelay: `${Math.min(i * 40, 320)}ms` }}>
              <div className="tm-top">
                <div className={`tm-avatar ${isAdmin(user.role) ? 'admin' : ''}`}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                </div>
                <span className="tm-role" style={rolePill(user.role)}>
                  <span className="ui-dot" style={{ background: rolePill(user.role).color }} />
                  {isAdmin(user.role) ? 'Administrator' : 'Member'}
                </span>
              </div>
              <div className="tm-name">{user.email.split('@')[0]}</div>
              <div className="tm-email">{user.email}</div>
              {!isAdmin(user.role) && (user.pages || []).length > 0 && (
                <div className="tm-pages">{(user.pages || []).length} page{(user.pages || []).length === 1 ? '' : 's'} of access</div>
              )}
              {isAdminUser && (
                <div className="tm-actions">
                  <button className="ui-btn ui-btn-ghost ui-btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => { setEditingUser(user); setFormData({ email: user.email, role: user.role, pages: user.pages || [] }); setShowModal(true); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    Edit
                  </button>
                  <button className="ui-btn ui-btn-danger ui-btn-sm" onClick={() => handleDelete(user._id)} aria-label="Remove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <style>{tmCss}</style>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(17,17,17,0.30)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#FFFFFF', width: '100%', maxWidth: '440px', borderRadius: '20px', padding: '28px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', border: '1px solid #E7E1D6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px' }}>
              <div className="ui-stat-ic tone-violet" style={{ width: '40px', height: '40px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
              </div>
              <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#111111', margin: 0, letterSpacing: '-0.03em' }}>
                {editingUser ? 'Edit Member' : 'Add New Member'}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#3A3A37', marginBottom: '7px' }}>Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                  placeholder="e.g. alex@company.com"
                  style={{ width: '100%', padding: '11px 13px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: editingUser ? '#FAF7F1' : '#fff', color: '#111111', fontFamily: "'IBM Plex Mono', monospace" }}
                />
              </div>
              {!editingUser && (
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#3A3A37', marginBottom: '7px' }}>Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="Set a secure password"
                    style={{ width: '100%', padding: '11px 13px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', fontSize: '14px', boxSizing: 'border-box', color: '#111111' }}
                  />
                </div>
              )}
              <div style={{ marginBottom: '26px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#3A3A37', marginBottom: '7px' }}>Access Level</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  style={{ width: '100%', padding: '11px 13px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', fontSize: '14px', background: 'white', boxSizing: 'border-box', color: '#111111' }}
                >
                  <option value="Member">Member</option>
                  <option value="Admin">Administrator</option>
                </select>
                {formData.role === 'Admin' && (
                  <div style={{ fontSize: '12px', color: '#2A5B8A', marginTop: '8px', background: '#E1ECF5', border: '1px solid #E7E1D6', borderRadius: '8px', padding: '9px 12px' }}>
                    Administrators have full access to every page and can manage members.
                  </div>
                )}
              </div>

              {formData.role === 'Member' && (
                <div style={{ marginBottom: '26px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#3A3A37', marginBottom: '4px' }}>Page Access</label>
                  <div style={{ fontSize: '12px', color: '#9C978C', marginBottom: '10px' }}>Tick the pages this member is allowed to see and use.</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {PAGES.map(p => {
                      const on = formData.pages.includes(p.key);
                      return (
                        <button
                          type="button"
                          key={p.key}
                          onClick={() => togglePage(p.key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left',
                            padding: '9px 11px', borderRadius: '999px', cursor: 'pointer',
                            border: `1px solid ${on ? '#111111' : '#E7E1D6'}`,
                            background: on ? '#111111' : '#fff',
                            transition: 'all .12s'
                          }}
                        >
                          <span style={{
                            width: '17px', height: '17px', borderRadius: '5px', flex: 'none',
                            border: `1.5px solid ${on ? '#F9F4EE' : '#9C978C'}`,
                            background: on ? '#F9F4EE' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: on ? '#F9F4EE' : '#3A3A37' }}>{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="ui-btn ui-btn-ghost"
                  style={{ flex: 1, justifyContent: 'center', padding: '11px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ui-btn"
                  style={{ flex: 1, justifyContent: 'center', padding: '11px' }}
                >
                  {editingUser ? 'Save Changes' : 'Invite User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const tmCss = `
.tm-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
.tm-card{ background:#fff; border:1px solid #E7E1D6; border-radius:14px; padding:20px; transition:box-shadow .15s, transform .15s, border-color .15s; animation:uiIn .4s ease both; }
.tm-card:hover{ box-shadow:4px 4px 0 rgba(17,17,17,.2); transform:translateY(-2px); border-color:#111111; }
.tm-top{ display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.tm-avatar{ width:52px; height:52px; border-radius:999px; background:#111111; color:#F9F4EE; display:flex; align-items:center; justify-content:center; flex:none; }
.tm-avatar.admin{ background:#111111; }
.tm-role{ display:inline-flex; align-items:center; gap:6px; font-size:10.5px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; padding:4px 10px; border-radius:999px; }
.tm-name{ font-size:16px; font-weight:700; color:#111111; letter-spacing:-0.03em; text-transform:capitalize; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tm-email{ font-size:13px; color:#6B6862; font-family:'IBM Plex Mono',monospace; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-top:2px; }
.tm-pages{ font-size:11.5px; color:#111111; background:#FAF7F1; border:1px solid #E7E1D6; display:inline-block; padding:2px 9px; border-radius:999px; margin-top:11px; font-weight:700; }
.tm-actions{ display:flex; gap:8px; margin-top:16px; padding-top:14px; border-top:1px solid #E7E1D6; }
`;

export default Users;
